import React, { useEffect, useState, useRef } from "react";
import { Spinner } from "reactstrap";
import { injectIntl } from "react-intl";

import { useFetch } from "../Base/Hooks/fetch";

import PageDisplay from "../Base/PageDisplay/PageDisplay";
import TableDisplay from "../Base/Shared/TableDisplay";
import SearchFilter from "../Base/Shared/SearchFilter";
import PopupMessage from "../Base/Popup/PopupMessage";

import SeasonRow from "./SeasonRow";
import { useFetchOnDemand } from "../Base/Hooks/fetchOnDemand";

const Seasons = (props) => {
  const { title, history, match, intl, mainState } = props;
  const { formatMessage } = intl;
  const [pageTitle, setPageTitle] = useState("");
  const [searchText, setSearchText] = useState("");
  const [initialValues, setInitialValues] = useState([]);
  const [showSavePopup, setShowSavePopup] = useState(false);

  const [tableData, setTableData] = useState([]);
  const { fetchOnDemand } = useFetchOnDemand();
  const pageRef = useRef();

  const {
    error,
    loading,
    data: seasonsData,
  } = useFetch(
    `/v1/${match.params.sport}/organizations/${match.params.organizationId}/seasons?limit=6000`,
    "",
    true,
    "location",
    true,
  );

  const columns = [
    formatMessage({
      id: "season.name",
      defaultMessage: "Season Name",
    }),
    formatMessage({
      id: "season.id",
      defaultMessage: "Season ID",
    }),
    formatMessage({
      id: "season.ui-type",
      defaultMessage: "User Interface",
    }),
    formatMessage({
      id: "season.default-roster",
      defaultMessage: "Default Roster",
    }),
    formatMessage({
      id: "season.allow-roster",
      defaultMessage: "Allow roster selection",
    }),
    formatMessage({
      id: "season.allow-officials",
      defaultMessage: "Allow match officials",
    }),
    formatMessage({
      id: "season.allow-multiple-captains",
      defaultMessage: "Allow multiple captains",
    }),
    formatMessage({
      id: "season.matchLockTime",
      defaultMessage: "Match Lock Time",
    }),
    "",
  ];

  const tooltips = [
    "",
    "",
    match.params.sport !== "hockey"
      ? formatMessage({
          id: "season.ui-type-tooltip",
          defaultMessage: "Select the Synergy Stats interface that this season requires.",
        })
      : formatMessage({
          id: "season.ui-type-hockey-tooltip",
          defaultMessage:
            "The user interface for Field Hockey is determined by the Discipline configured for a season within the Competition Management product",
        }),
    formatMessage({
      id: "season.default-roster-tooltip",
      defaultMessage: "This roster will display when the team setup page is opened in Synergy Stats",
    }),
    formatMessage({
      id: "season.allow-roster-tooltip",
      defaultMessage: "Allow user to fetch alternative roster for a match",
    }),
    formatMessage({
      id: "season.allow-officials-tooltip",
      defaultMessage: "Allow user to add match officials to a match",
    }),
    formatMessage({
      id: "season.allow-multiple-captains-tooltip",
      defaultMessage: "Allow user to select multiple captains for a match",
    }),
    formatMessage({
      id: "season.matchLockTime-tooltip",
      defaultMessage: "After a match is confirmed, the match will be locked after 'Match Lock Time' minutes",
    }),
    "",
  ];

  useEffect(() => {
    let data = seasonsData !== null ? seasonsData.data : [];
    if (data) {
      setInitialValues([...data]);
    }
  }, [seasonsData]);

  useEffect(() => {
    if (seasonsData) {
      setPageTitle(mainState.currentOrganizationName ? mainState.currentOrganizationName : "");

      let filteredData = seasonsData !== null ? seasonsData.data : [];

      if (searchText !== "") {
        filteredData = filteredData.filter((result) => {
          return result.name.toUpperCase().indexOf(searchText.toUpperCase()) > -1;
        });
      }

      filteredData = filteredData.sort(function (a, b) {
        var aMixed = a["name"];
        var bMixed = b["name"];
        return aMixed < bMixed ? -1 : 1;
      });

      setTableData(filteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsData, searchText]);

  useEffect(() => {
    if (showSavePopup) {
      setTimeout(() => {
        setShowSavePopup(false);
      }, 5000);
    }
  }, [showSavePopup]);

  const handleSettingsChange = (seasonData, propName, value) => {
    const body = {
      seasonId: seasonData.seasonId,
      defaultRoster: propName === "defaultRoster" ? value : seasonData.defaultRoster,
      allowRosterSelect: propName === "allowRosterSelect" ? !!value : !!seasonData.allowRosterSelect,
      allowMatchOfficials: propName === "allowMatchOfficials" ? !!value : !!seasonData.allowMatchOfficials,
      allowMultipleCaptains: propName === "allowMultipleCaptains" ? !!value : !!seasonData.allowMultipleCaptains,
      matchLockTime: propName === "matchLockTime" ? value : seasonData.matchLockTime,
      uiType: propName === "uiType" ? value : seasonData.uiType,
    };
    fetchOnDemand(`/v1/${match.params.sport}/organizations/${match.params.organizationId}/seasons`, {
      method: "PATCH",
      values: body,
    }).then(() => {
      const newTableData = tableData.map((season) => {
        let newSeason = { ...season };
        if (newSeason.seasonId === seasonData.seasonId) {
          newSeason[propName] = value;
        }
        return newSeason;
      });
      setTableData(newTableData);
      setShowSavePopup(true);
    });
  };

  return (
    <React.Fragment>
      {loading && <Spinner size="md" color="orange" />}
      {!loading && (
        <PageDisplay
          title={
            pageTitle +
            " " +
            formatMessage({
              id: "seasons.active",
              defaultMessage: "Active Seasons",
            })
          }
          error={error}
          pageTitle={
            pageTitle +
            formatMessage({
              id: "seasons.active",
              defaultMessage: "Active Seasons",
            }) +
            " - " +
            title
          }
          ref={pageRef}
          history={history}
        >
          <SearchFilter doSearch={setSearchText} />
          {showSavePopup && (
            <PopupMessage
              text={formatMessage({
                id: "season.saved",
                defaultMessage: "Changes have been saved",
              })}
            />
          )}
          <TableDisplay
            containerClass="table-responsive"
            columns={columns}
            tooltips={tooltips}
            rows={tableData.map((season, index) => (
              <SeasonRow
                key={index}
                itemIndex={index}
                result={season}
                initialValues={initialValues?.find((entry) => entry.seasonId === season.seasonId)}
                onSettingsChange={(propName, value) => {
                  handleSettingsChange(season, propName, value);
                }}
                sport={match.params.sport}
                intl={intl}
              />
            ))}
            loading={loading}
          />
        </PageDisplay>
      )}
    </React.Fragment>
  );
};

export default injectIntl(Seasons);
